import {
  Activity, ArrowDown, ArrowRight, Ban, Check, Clock, FlaskConical, Globe2, Play, RotateCcw,
  UserCheck, X, Zap, Hand, Eye, Pause, SkipForward,
} from "lucide-react";
import { LIVE_SYSTEMS, METRICS_META } from "@/lib/liveOps";

export const ATTRIBUTION_COLORS = {
  "Assisted": "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  "Human Assisted": "text-sky-400 border-sky-500/40 bg-sky-500/10",
  "Organic": "text-amber-400 border-amber-500/40 bg-amber-500/10",
  "Excluded": "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
};

export const TRACKER_STEPS = [
  ["event", "EVENT"], ["state", "DETECTION"], ["policy", "POLICY"],
  ["decision", "DECISION"], ["action", "ACTION"], ["outcome", "OUTCOME"],
];

const fmtSla = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const severityTone = (sev) => {
  if (sev === "Critical" || sev === "High") return "text-red-400 border-red-500/40 bg-red-500/10";
  if (sev === "None" || sev === "—") return "text-zinc-400 border-zinc-600 bg-zinc-500/10";
  return "text-amber-400 border-amber-500/40 bg-amber-500/10";
};

export const classificationTone = (c) =>
  (c.includes("HIGH") || c.includes("SENSITIVE") || c.includes("HIGH-VALUE"))
    ? "text-red-400 border-red-500/40 bg-red-500/10"
    : "text-amber-400 border-amber-500/40 bg-amber-500/10";

export const decisionTone = (t) => {
  if (t.includes("HUMAN")) return "text-sky-400 border-sky-500/40 bg-sky-500/10";
  if (t.startsWith("NO ACTION")) return "text-zinc-400 border-zinc-600 bg-zinc-500/10";
  return "text-emerald-400 border-emerald-500/40 bg-emerald-500/10";
};

export const actionTitle = (kind) => {
  if (kind === "human") return "Action — CRM task created (human-in-the-loop)";
  if (kind === "none") return "Action — none taken";
  return "Action triggered";
};

export const blockedLabel = (type) => (type.startsWith("NO ACTION") ? type : "AUTOMATION BLOCKED");

export const Section = ({ title, testId, accent = "text-zinc-200", children, visible }) => {
  if (!visible) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 fade-slide-in" data-testid={testId}>
      <div className={`text-[10px] font-mono tracking-widest uppercase mb-3 ${accent}`}>{title}</div>
      {children}
    </div>
  );
};

export const DemoHeader = ({ demoActive, demoPaused, demoSeqIdx, seqLength, eventsToday, onRunDemo, onTogglePause, onNext, onRestart, onReplay }) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
        <Activity className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Live Operational Simulation
      </h1>
      <p className="text-sm text-zinc-400 mt-1">
        Cause → Decision → Action → Outcome across a live synthetic client book. Simulated feeds only — nothing here is live.
      </p>
    </div>
    <div className="flex flex-wrap items-center gap-3">
      {demoActive && (
        <span data-testid="live-demo-status" className="flex items-center gap-2 px-3 py-2 rounded-sm border border-red-500/40 bg-red-500/10 text-[11px] font-mono font-semibold text-red-400 tracking-widest">
          <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          LIVE DEMO RUNNING · SCENARIO {demoSeqIdx + 1}/{seqLength}
        </span>
      )}
      <div className="text-right">
        <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Events today</div>
        <div className="font-mono text-emerald-400 font-semibold" data-testid="events-today-counter">{eventsToday.toLocaleString()}</div>
      </div>
      {!demoActive ? (
        <button
          data-testid="run-live-demo-button"
          onClick={onRunDemo}
          className="flex items-center gap-2 px-3.5 py-2.5 lg:py-2 rounded-sm text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-zinc-950 transition-colors duration-200 shadow-[0_0_10px_rgba(16,185,129,0.2)] hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]"
        >
          <Play className="w-3.5 h-3.5" strokeWidth={1.5} /> Run Live Demo
        </button>
      ) : (
        <>
          <button
            data-testid="demo-pause-button"
            onClick={onTogglePause}
            className="flex items-center gap-2 px-3.5 py-2.5 lg:py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200"
          >
            {demoPaused ? <Play className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} /> : <Pause className="w-3.5 h-3.5 text-amber-400" strokeWidth={1.5} />}
            {demoPaused ? "Continue" : "Pause"}
          </button>
          <button
            data-testid="demo-next-event-button"
            onClick={onNext}
            disabled={!demoPaused}
            className="flex items-center gap-2 px-3.5 py-2.5 lg:py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <SkipForward className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} /> Next Event
          </button>
          <button
            data-testid="demo-restart-button"
            onClick={onRestart}
            className="flex items-center gap-2 px-3.5 py-2.5 lg:py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200"
          >
            <RotateCcw className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} /> Restart Demo
          </button>
        </>
      )}
      <button
        data-testid="replay-button"
        onClick={onReplay}
        className="flex items-center gap-2 px-3.5 py-2.5 lg:py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200"
      >
        <RotateCcw className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} /> Replay simulation
      </button>
    </div>
  </div>
);

export const DemoSummary = ({ demoEvents, demoStats, onRestart }) => {
  const items = [
    ["Operational Events Processed", demoEvents],
    ["Automated Actions", demoStats.automated],
    ["Human Escalations", demoStats.escalations],
    ["Actions Blocked by Governance", demoStats.suppressed],
    ["Assisted FTD", `€${demoStats.assistedRevenue.toLocaleString()}`],
    ["Assisted Re-engagement", `${demoStats.reengaged} Client`],
    ["Organic FTD", demoStats.organic > 0 ? "£3,000" : "—"],
  ];
  return (
    <div className="bg-zinc-900 border border-emerald-500/30 rounded-md p-6 shadow-[0_0_15px_rgba(16,185,129,0.1)] fade-slide-in" data-testid="demo-summary">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm font-bold text-emerald-400 font-display tracking-wide">LIVE DEMO COMPLETE</div>
        <button
          data-testid="demo-restart-summary-button"
          onClick={onRestart}
          className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-[11px] font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200"
        >
          <RotateCcw className="w-3 h-3 text-emerald-400" strokeWidth={1.5} /> Restart Demo
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
        {items.map(([k, v]) => (
          <div key={k} className="bg-zinc-950/60 border border-zinc-800 rounded-sm p-3">
            <div className="text-[9px] font-semibold text-zinc-500 uppercase tracking-wider">{k}</div>
            <div className="text-base font-bold font-mono text-emerald-400 mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-800 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10px] font-mono tracking-widest text-zinc-500">CAUSE → DECISION → ACTION → OUTCOME — the operational model demonstrated</span>
        <span className="text-[10px] font-mono text-amber-500/80">Synthetic demonstration environment · simulated events only</span>
      </div>
    </div>
  );
};

export const MetricsStrip = ({ metrics }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="live-metrics">
    {METRICS_META.map((mm) => (
      <div key={mm.key} className="bg-zinc-900 border border-zinc-800 rounded-md p-3.5" data-testid={`metric-${mm.key}`}>
        <div className="text-[9px] font-semibold text-zinc-500 uppercase tracking-wider">{mm.label}</div>
        <div className="text-lg font-bold font-mono text-emerald-400 mt-1">
          {mm.money ? `€${metrics[mm.key].toLocaleString()}` : metrics[mm.key]}
        </div>
      </div>
    ))}
  </div>
);

export const SystemsStrip = ({ flow, maxChip }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="connected-systems">
    <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-3">Connected systems</div>
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      {LIVE_SYSTEMS.map((s) => (
        <div key={s.key} className="flex items-center gap-2" data-testid={`system-${s.key}`}>
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-semibold text-zinc-200">{s.name}</span>
          <span className="text-[9px] font-mono text-emerald-500 tracking-widest">LIVE</span>
          <span className="text-[9px] font-mono text-zinc-600">(simulated)</span>
        </div>
      ))}
    </div>
    <div className="flex flex-wrap items-center gap-1.5 mt-4 pt-3 border-t border-zinc-800" data-testid="data-flow-path">
      {flow.map((node, i) => (
        <span key={`${node}-${i}`} className="flex items-center gap-1.5">
          <span className={`px-2.5 py-1 rounded-sm text-[10px] font-mono border transition-colors duration-500 ${
            i <= maxChip ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-zinc-800 text-zinc-600"
          }`}>{node}</span>
          {i < flow.length - 1 && <ArrowRight className="w-3 h-3 text-zinc-600" strokeWidth={1.5} />}
        </span>
      ))}
    </div>
  </div>
);

export const CaseBoard = ({ scenarios, scenarioId, progressed, demoActive, onSelect, intel }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5" data-testid="case-board">
    {scenarios.map((s) => {
      const done = progressed.has(s.id);
      const active = scenarioId === s.id;
      return (
        <button
          key={s.id}
          data-testid={`scenario-tab-${s.id}`}
          onClick={() => onSelect(s.id)}
          className={`text-left bg-zinc-900 border rounded-md p-3 transition-colors duration-200 ${
            active ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-800 hover:border-zinc-600"
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-zinc-100 truncate">{s.client.name}</span>
            <span className="text-[9px] font-mono text-zinc-500 shrink-0">{s.category}</span>
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{s.client.country} · {s.client.language}</div>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {demoActive && active && (
              <span className="px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold border text-red-400 border-red-500/40 bg-red-500/10 animate-pulse" data-testid="live-event-badge">
                LIVE EVENT
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold border ${done ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-amber-400 border-amber-500/40 bg-amber-500/10"}`}>
              {done ? s.boardTo : s.boardFrom}
            </span>
            {done && (
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono border ${ATTRIBUTION_COLORS[s.attribution]}`}>
                {s.attribution}
              </span>
            )}
          </div>
          {intel && intel[s.id] && (
            <div className="mt-2 pt-2 border-t border-zinc-800/70 flex items-center justify-between gap-2" data-testid={`intel-chip-${s.id}`}>
              <span className="text-[9px] font-mono font-semibold text-emerald-500/90 tracking-wider truncate">{intel[s.id].classification}</span>
              <span className="text-[9px] font-mono text-zinc-500 shrink-0">{intel[s.id].score}/100</span>
            </div>
          )}
        </button>
      );
    })}
  </div>
);

export const StepTracker = ({ trackerIdx, clientName }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md px-5 py-3 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="live-step-tracker">
    <span className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Live step</span>
    {TRACKER_STEPS.map(([phase, label], i) => {
      const state = i < trackerIdx ? "done" : i === trackerIdx ? "current" : "pending";
      return (
        <span key={phase} className={`flex items-center gap-1.5 text-[10px] font-mono font-semibold tracking-wider ${
          state === "done" ? "text-emerald-400" : state === "current" ? "text-amber-400" : "text-zinc-600"
        }`} data-testid={`tracker-${label.toLowerCase()}`}>
          {state === "done" && <Check className="w-3 h-3" />}
          {state === "current" && <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />}
          {state === "pending" && <span className="w-2 h-2 rounded-full border border-zinc-700" />}
          {label}
        </span>
      );
    })}
    <span className="ml-auto text-[10px] font-mono text-zinc-500">{clientName} — LIVE EVENT</span>
  </div>
);

const ProfileCard = ({ sc }) => {
  const fields = [
    ["Country", sc.client.country], ["Language", sc.client.language],
    ["Account entity", sc.client.jurisdiction], ["Account value", sc.client.value],
    ["Value segment", sc.client.segment], ["Time in current state", sc.condition.timeInState],
  ];
  const sev = sc.condition.severity;
  const showSev = sev !== "—" && sev !== "None";
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="profile-card">
      <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-3">Client profile &amp; current condition</div>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-base font-bold text-zinc-100">{sc.client.name}</span>
        <span className="font-mono text-xs text-zinc-500">{sc.client.ref}</span>
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${severityTone(sev)}`} data-testid="condition-badge">
          {sc.condition.label}{showSev ? ` · ${sev}` : ""}
        </span>
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono border ${ATTRIBUTION_COLORS[sc.attribution]}`} data-testid="attribution-badge">
          Attribution: {sc.attribution}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        {fields.map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{k}</div>
            <div className="text-zinc-200 mt-0.5">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StateTransition = ({ sc, stateUnchanged }) => (
  <div className="flex flex-wrap items-center gap-3">
    {stateUnchanged ? (
      <span className="px-3 py-1.5 rounded-sm text-xs font-semibold border border-emerald-500/40 bg-emerald-500/10 text-emerald-400" data-testid="state-to-badge">
        {sc.stateTransition.to} — no change
      </span>
    ) : (
      <>
        <span className="px-3 py-1.5 rounded-sm text-xs font-semibold border border-zinc-700 bg-zinc-800/60 text-zinc-300">{sc.stateTransition.from}</span>
        <ArrowRight className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
        <span className="px-3 py-1.5 rounded-sm text-xs font-semibold border border-red-500/40 bg-red-500/10 text-red-400" data-testid="state-to-badge">{sc.stateTransition.to}</span>
      </>
    )}
    <span className={`ml-auto px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold border ${classificationTone(sc.classification)}`} data-testid="classification-badge">
      {sc.classification}
    </span>
  </div>
);

const AutomatedAction = ({ sc, delivered, opened }) => (
  <div className="space-y-3">
    <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
      <span className="font-semibold text-zinc-100">{sc.action.channel}</span>
      <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
      <span>{sc.action.language}</span>
      <span className="font-mono text-[10px] text-zinc-600">{sc.action.t}</span>
      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-400">Template {sc.action.template} · approved</span>
    </div>
    {sc.action.channelOptions && (
      <div className="flex flex-wrap items-center gap-2 border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-950/60" data-testid="channel-selection">
        <span className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mr-1">Channel selection:</span>
        {sc.action.channelOptions.map((o) => {
          const tone = o.selected ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : o.allowed ? "border-zinc-700 text-zinc-400" : "border-red-500/30 text-red-400/70 line-through";
          return (
            <span key={o.channel} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-mono border ${tone}`}>
              {o.allowed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              {o.channel}{o.selected ? " · selected" : ""}
            </span>
          );
        })}
      </div>
    )}
    <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 border border-zinc-800 rounded-sm px-2.5 py-1.5 bg-zinc-950/60" data-testid="multilingual-note">
      <Globe2 className="w-3 h-3 text-emerald-400" strokeWidth={1.5} />
      Client profile ({sc.client.language}) → policy (approved) → template {sc.action.template} → personalised {sc.action.language} communication
    </div>
    <div className="border-l-2 border-emerald-500/50 bg-zinc-950/60 rounded-sm px-4 py-3 text-xs text-zinc-300 italic leading-relaxed" data-testid="action-message">
      “{sc.action.message}”
    </div>
    <div className="flex items-center gap-2 text-[10px] font-mono" data-testid="delivery-status">
      <span className="text-emerald-400">Sent</span>
      <ArrowRight className="w-3 h-3 text-zinc-600" />
      <span className={delivered ? "text-emerald-400" : "text-zinc-600"}>Delivered</span>
      <ArrowRight className="w-3 h-3 text-zinc-600" />
      <span className={opened ? "text-emerald-400" : "text-zinc-600"}>Opened</span>
    </div>
    {sc.action.task && (
      <div className="flex items-center gap-2 text-[11px] text-sky-400 border border-sky-500/30 bg-sky-500/5 rounded-sm px-3 py-2">
        <UserCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
        CRM task also created → {sc.action.task.assignee} · priority {sc.action.task.priority} · SLA {sc.action.task.sla}
      </div>
    )}
  </div>
);

const SlaTimer = ({ breach, sla, slaFloor }) => {
  let content;
  if (breach) {
    content = <span className="text-xs font-bold text-red-400">SLA BREACH → Team Lead Escalation Triggered</span>;
  } else if (sla !== null && sla <= slaFloor) {
    content = <span className="text-xs text-emerald-400 font-semibold">Accepted within SLA — human intervention in progress</span>;
  } else {
    content = <span className="text-xs text-zinc-300">Human task SLA — <span className="font-mono text-amber-400 font-semibold">{fmtSla(sla ?? 0)}</span> remaining</span>;
  }
  return (
    <div className="flex items-center gap-3 border border-zinc-800 rounded-sm px-4 py-3 bg-zinc-950/60" data-testid="sla-timer">
      <Clock className={`w-4 h-4 ${breach ? "text-red-400" : "text-amber-400"}`} strokeWidth={1.5} />
      {content}
    </div>
  );
};

const HumanTask = ({ sc, breach, sla, slaFloor }) => (
  <div className="space-y-3">
    <div className="flex flex-wrap items-center gap-3 text-xs">
      <span className="px-2.5 py-1 rounded-sm font-bold text-sky-400 border border-sky-500/40 bg-sky-500/10">CRM TASK CREATED</span>
      <span className="text-zinc-400">Assigned to: <span className="text-zinc-100 font-semibold">{sc.action.task.assignee}</span></span>
      <span className="text-zinc-400">Priority: <span className="text-red-400 font-semibold">{sc.action.task.priority}</span></span>
    </div>
    <SlaTimer breach={breach} sla={sla} slaFloor={slaFloor} />
    <div>
      <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-2">Context attached</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {sc.action.task.context.map((ctx) => (
          <div key={ctx} className="text-[11px] text-zinc-400 flex items-start gap-1.5">
            <span className="w-1 h-1 rounded-full bg-emerald-500 mt-1.5 shrink-0" /> {ctx}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const NoAction = ({ sc }) => {
  const isSuppression = sc.attribution === "Excluded" && sc.decision.type.includes("SUPPRESSION");
  return (
    <div className="flex items-start gap-3 border border-zinc-800 rounded-sm px-4 py-3.5 bg-zinc-950/60" data-testid="no-action-card">
      {isSuppression
        ? <Hand className="w-4 h-4 text-red-400 mt-0.5 shrink-0" strokeWidth={1.5} />
        : <Eye className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" strokeWidth={1.5} />}
      <div className="text-xs text-zinc-300 leading-relaxed">{sc.action.noneReason}</div>
    </div>
  );
};

export const DetailColumn = ({ sc, phases, delivered, opened, breach, sla, slaFloor, responseEvents, intelligence }) => {
  const policyPassed = !sc.policy.blocked;
  const stateUnchanged = sc.stateTransition.from === sc.stateTransition.to;
  const outcomeFields = [
    ["Intervention", sc.outcome.intervention], ["Language", sc.outcome.language],
    ["Time to outcome", sc.outcome.time], ["Classification", sc.outcome.classification],
    ["CRM status", sc.outcome.crm], ["Attribution", sc.outcome.attribution],
  ];
  return (
    <div className="xl:col-span-2 space-y-4">
      <ProfileCard sc={sc} />
      {intelligence}

      <Section title="Event Detected — the cause" testId="live-event-card" accent="text-amber-400" visible={phases.has("event")}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="font-mono text-lg font-semibold text-zinc-100">{sc.event.t}</span>
          <span className="text-zinc-500">→</span>
          <span className="text-sm font-semibold text-zinc-100">{sc.event.source}</span>
          <span className="text-zinc-500">→</span>
          <span className="text-sm font-semibold text-amber-400">{sc.event.type}</span>
          {sc.event.amount && <span className="font-mono text-sm text-zinc-100">{sc.event.amount}</span>}
        </div>
      </Section>

      <Section title="Client lifecycle state — updated by O.N.E.Tech" testId="live-state-card" visible={phases.has("state")}>
        <StateTransition sc={sc} stateUnchanged={stateUnchanged} />
      </Section>

      <Section title="Why this was detected" testId="why-detected" accent="text-emerald-400" visible={phases.has("state")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {sc.why.map((w) => (
            <div key={w} className="flex items-start gap-2 text-xs text-zinc-300">
              <Zap className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" strokeWidth={1.5} /> {w}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Policy & eligibility check" testId="policy-check" accent="text-sky-400" visible={phases.has("policy")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {sc.policy.checks.map((c) => {
            const failed = c.includes("FAILED");
            return (
              <div key={c} className="flex items-start gap-2 text-xs">
                {failed
                  ? <X className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                  : <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />}
                <span className={failed ? "text-red-400" : "text-zinc-300"}>{c.replace(" — FAILED", "")}</span>
              </div>
            );
          })}
        </div>
        {!policyPassed && (
          <div className="mt-4 flex items-center gap-2 border border-red-500/40 bg-red-500/10 rounded-sm px-3 py-2.5" data-testid="automation-blocked">
            <Ban className="w-4 h-4 text-red-400" strokeWidth={1.5} />
            <div>
              <span className="text-xs font-bold text-red-400 tracking-wide">{blockedLabel(sc.decision.type)}</span>
              <span className="text-xs text-zinc-300 ml-2">{sc.policy.blockReason}</span>
            </div>
          </div>
        )}
        {sc.policyBoundary && (
          <div className="mt-4 border border-zinc-800 rounded-sm p-3.5 bg-zinc-950/60" data-testid="policy-boundary">
            <div className="text-[10px] font-mono tracking-widest text-red-400 uppercase mb-2">Withdrawal non-interference — O.N.E.Tech does NOT:</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {sc.policyBoundary.map((b) => (
                <div key={b} className="flex items-start gap-2 text-[11px] text-zinc-400">
                  <Ban className="w-3 h-3 text-red-400 mt-0.5 shrink-0" /> {b}
                </div>
              ))}
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">Permitted: broker-approved status communication only, or a human support task.</div>
          </div>
        )}
      </Section>

      <Section title="Decision" testId="decision-card" accent="text-emerald-400" visible={phases.has("decision")}>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className={`px-3 py-1.5 rounded-sm text-xs font-bold tracking-wide border ${decisionTone(sc.decision.type)}`} data-testid="decision-type">
            DECISION: {sc.decision.type}
          </span>
          {sc.decision.priority !== "—" && <span className="text-xs text-zinc-400">Priority: <span className="text-red-400 font-semibold">{sc.decision.priority}</span></span>}
          {sc.decision.confidence !== "—" && <span className="text-xs text-zinc-400">Confidence: <span className="font-mono text-emerald-400">{sc.decision.confidence}</span></span>}
        </div>
        <div className="text-xs text-zinc-300 space-y-1">
          <div><span className="text-zinc-600">Workflow: </span>{sc.decision.workflow}</div>
          <div><span className="text-zinc-600">Reason: </span>{sc.decision.reason}</div>
          {sc.decision.channel !== "—" && <div><span className="text-zinc-600">Channel / Language: </span>{sc.decision.channel} · {sc.decision.language}</div>}
        </div>
      </Section>

      <Section title={actionTitle(sc.action.kind)} testId="action-card" accent="text-violet-400" visible={phases.has("action")}>
        {sc.action.kind === "automated" && <AutomatedAction sc={sc} delivered={delivered} opened={opened} />}
        {sc.action.kind === "human" && <HumanTask sc={sc} breach={breach} sla={sla} slaFloor={slaFloor} />}
        {sc.action.kind === "none" && <NoAction sc={sc} />}
      </Section>

      <Section title="Client / system response" testId="response-card" accent="text-amber-400" visible={phases.has("response")}>
        <div className="space-y-2">
          {responseEvents.map((e) => (
            <div key={`${e.t}-${e.text}`} className="flex items-center gap-3 text-xs fade-slide-in">
              <span className="font-mono text-zinc-500">{e.t}</span>
              <span className={e.breach ? "text-red-400 font-semibold" : "text-zinc-300"}>{e.text}</span>
              <span className="text-[10px] font-mono text-zinc-600">· {e.system}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Lifecycle state update — recognised automatically" testId="state-update-card" visible={phases.has("update")}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="px-3 py-1.5 rounded-sm text-xs font-semibold border border-zinc-700 bg-zinc-800/60 text-zinc-300">{sc.stateFinal.from}</span>
          <ArrowRight className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
          <span className="px-3 py-1.5 rounded-sm text-xs font-semibold border border-emerald-500/40 bg-emerald-500/10 text-emerald-400" data-testid="state-final-badge">{sc.stateFinal.to}</span>
        </div>
      </Section>

      <Section title="Outcome" testId="outcome-card" accent="text-emerald-400" visible={phases.has("outcome")}>
        <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-md p-5 shadow-[0_0_15px_rgba(16,185,129,0.1)]">
          <div className="flex flex-wrap items-baseline gap-3 mb-4">
            <span className="text-lg font-bold text-emerald-400 font-display">{sc.outcome.title}</span>
            <span className="text-2xl font-bold font-mono text-zinc-100">{sc.outcome.value}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {outcomeFields.map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{k}</div>
                <div className="text-zinc-200 mt-0.5 font-semibold">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
};

export const TimelinePanel = ({ timeline, clientName }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="live-timeline">
    <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-4">Operational event history — {clientName}</div>
    {timeline.length === 0 && (
      <div className="flex items-center gap-2 text-xs text-zinc-600"><Play className="w-3 h-3 animate-pulse" /> Simulation starting…</div>
    )}
    <div className="space-y-0">
      {timeline.map((e, i) => (
        <div key={`${e.t}-${i}`} className="fade-slide-in">
          <div className="flex items-start gap-3">
            <span className="font-mono text-[10px] text-zinc-500 mt-0.5 w-14 shrink-0">{e.t}</span>
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${e.breach ? "bg-red-400" : e.phase === "outcome" ? "bg-emerald-400" : "bg-zinc-600"}`} />
            <div className="min-w-0">
              <div className={`text-[11px] leading-relaxed ${e.breach ? "text-red-400 font-semibold" : "text-zinc-300"}`}>{e.text}</div>
              <div className="text-[9px] font-mono text-zinc-600">{e.system}</div>
            </div>
          </div>
          {i < timeline.length - 1 && (
            <div className="pl-[68px] py-0.5"><ArrowDown className="w-3 h-3 text-zinc-700" strokeWidth={1.5} /></div>
          )}
        </div>
      ))}
    </div>
  </div>
);

export const EventStream = ({ stream }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="event-stream">
    <div className="flex items-center justify-between mb-4">
      <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Brokerage event feed — all clients</div>
      <span className="flex items-center gap-1.5 text-[9px] font-mono text-emerald-500"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> STREAMING (SIMULATED)</span>
    </div>
    <div className="space-y-2.5">
      {stream.length === 0 && <div className="text-xs text-zinc-600">Listening for events…</div>}
      {stream.map((e, i) => (
        <div key={`${e.ts}-${e.text}-${i}`} className="text-[11px] fade-slide-in">
          <span className="font-mono text-zinc-500">{e.ts}</span>
          <span className="text-zinc-600"> | </span>
          <span className={`font-semibold ${e.scripted ? "text-emerald-400" : "text-zinc-300"}`}>{e.system}</span>
          <span className="text-zinc-600"> | </span>
          <span className="text-zinc-400">{e.text}</span>
          {e.amount && <span className="font-mono text-emerald-400"> {e.amount}</span>}
        </div>
      ))}
    </div>
  </div>
);

export const LiveDisclaimer = () => (
  <div className="border border-amber-500/20 bg-amber-500/5 rounded-sm px-4 py-3 text-[10px] font-mono text-amber-500/90 flex items-start gap-2" data-testid="live-disclaimer">
    <FlaskConical className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5} />
    Synthetic demonstration environment — events, timers, messages and outcomes are simulated to show operational flow. No live systems, clients or funds.
  </div>
);
